import { z } from 'zod';

import { roleSchema, scopeTypeSchema } from './roles.js';

/**
 * Equipo: miembros y roles por cliente/empresa, invitaciones pendientes, recordatorios de 2FA.
 * Un client_admin ve y gestiona sólo su cliente/empresa y sólo con roles de cliente.
 */

const isoDate = z.string();

export const teamMembershipSchema = z.object({
  id: z.uuid(),
  scopeType: scopeTypeSchema,
  scopeId: z.uuid(),
  scopeName: z.string(),
  role: roleSchema,
  /** La persona que mira puede cambiar/quitar esta membresía. */
  manageable: z.boolean(),
});

export const teamMemberSchema = z.object({
  userId: z.uuid(),
  email: z.string(),
  name: z.string(),
  status: z.enum(['active', 'disabled']),
  totpEnabled: z.boolean(),
  lastLoginAt: isoDate.nullable(),
  /** Foto de perfil (mismo origen); null = iniciales. */
  avatarUrl: z.string().nullable().optional(),
  memberships: z.array(teamMembershipSchema),
});
export type TeamMember = z.infer<typeof teamMemberSchema>;

export const teamInvitationSchema = z.object({
  id: z.uuid(),
  email: z.string(),
  scopeType: scopeTypeSchema,
  scopeId: z.uuid(),
  scopeName: z.string(),
  role: roleSchema,
  expiresAt: isoDate,
  createdAt: isoDate,
  invitedByEmail: z.string().nullable(),
  expired: z.boolean(),
});
export type TeamInvitation = z.infer<typeof teamInvitationSchema>;

export const teamScopeSchema = z.object({
  scopeType: scopeTypeSchema,
  scopeId: z.uuid(),
  label: z.string(),
  roles: z.array(roleSchema),
});
export type TeamScope = z.infer<typeof teamScopeSchema>;

/** Política de 2FA de un cliente que la persona puede cambiar (Equipo). */
export const teamMfaPolicySchema = z.object({
  clientId: z.uuid(),
  clientName: z.string(),
  requireMfa: z.boolean(),
  /** Personas activas del cliente (membresías de cliente/empresa) que todavía no tienen 2FA. */
  membersWithoutMfa: z.number().int(),
  membersTotal: z.number().int(),
});
export type TeamMfaPolicy = z.infer<typeof teamMfaPolicySchema>;

export const teamResponseSchema = z.object({
  members: z.array(teamMemberSchema),
  invitations: z.array(teamInvitationSchema),
  /** Alcances a los que la persona puede invitar, con los roles permitidos en cada uno. */
  scopes: z.array(teamScopeSchema),
  /** Clientes cuya política de 2FA puede cambiar la persona (agencia owner/admin o client_admin). */
  mfaPolicies: z.array(teamMfaPolicySchema).optional(),
});
export type TeamResponse = z.infer<typeof teamResponseSchema>;

export const updateMembershipRoleSchema = z.object({ role: roleSchema });

export const updateMfaPolicySchema = z.object({ requireMfa: z.boolean() });
export type UpdateMfaPolicy = z.infer<typeof updateMfaPolicySchema>;
